import { Helmet } from 'react-helmet-async';
import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Plus,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface SupportStats {
  awaitingResponse: number;
  resolvedThisMonth: number;
}

interface SupportTicket {
  id: number;
  title: string;
  category: string;
  priority: string;
  status: string;
  description: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

const SupportCenterPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<SupportStats>({
    awaitingResponse: 0,
    resolvedThisMonth: 0,
  });
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [supportPhone, setSupportPhone] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const hasCheckedAuth = useRef(false);

  const timeAgo = (dateStr: string) => {
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const categories = [
    'Job & Scheduling',
    'Payments & Invoices',
    'Account & Verification',
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' },
  ];

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user?.id || authLoading) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, ticketsRes, contactRes] = await Promise.all([
          apiService.get('/provider/support/stats'),
          apiService.get('/provider/support/tickets'),
          apiService.get('/public/contact-info'),
        ]);
        if (statsRes.success) setStats(statsRes.data);
        if (ticketsRes.success) setTickets(ticketsRes.data || []);
        if (contactRes.success && contactRes.data?.phone) setSupportPhone(contactRes.data.phone);
      } catch (error) {
        console.error('Error fetching support center data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id, authLoading]);

  const openCases = tickets.filter(t => t.status === 'open' || t.status === 'pending');
  const resolvedCases = tickets.filter(t => t.status === 'resolved');

  const handleNewTicket = () => {
    if (!selectedCategory || !selectedPriority) {
      toast({
        title: 'Please select category and priority',
        variant: 'destructive',
      });
      return;
    }
    navigate('/provider/support/new');
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Support Center - VireWire Provider</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Support Center</h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage your tickets and access platform resources
          </p>
        </div>

        {/* Emergency Alert */}
        {supportPhone && (
          <div className="bg-yellow-50 border-l-4 border-l-yellow-400 p-4 rounded">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-bold text-yellow-800">EMERGENCY HOTLINE</p>
                <p className="text-sm text-yellow-700 mt-1">
                  For urgent issues, call support hotline {supportPhone}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Stats and New Ticket */}
          <div className="space-y-6">
            {/* Support Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-4xl font-bold text-gray-900">{stats.awaitingResponse}</p>
                <p className="text-xs text-gray-600 uppercase font-semibold mt-1">
                  Awaiting Response
                </p>
                <p className="text-xs text-gray-500 mt-2">Check your active support tickets</p>
              </div>

              <div className="text-center">
                <CheckCircle className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <p className="text-4xl font-bold text-gray-900">{stats.resolvedThisMonth}</p>
                <p className="text-xs text-gray-600 uppercase font-semibold mt-1">
                  Resolved This Month
                </p>
                <p className="text-xs text-gray-500 mt-2">Successfully closed issues</p>
              </div>
            </div>

            {/* New Support Request */}
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="text-base">New Support Request</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    CATEGORY
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded py-2 px-3 text-sm"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    PRIORITY
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {priorities.map((p) => (
                      <button
                        key={p.value}
                        onClick={() => setSelectedPriority(p.value)}
                        className={`py-2 px-3 rounded text-sm font-semibold transition-colors ${
                          selectedPriority === p.value
                            ? p.color
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handleNewTicket}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Request
                </Button>
              </CardContent>
            </Card>

            {/* Common Topics */}
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Common Topics</CardTitle>
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                <div className="text-sm">
                  <p className="font-semibold text-gray-900 mb-1">What is Platform Fee?</p>
                  <p className="text-xs text-gray-600">Platform takes 20% from your earnings...</p>
                </div>
                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-semibold text-gray-900 mb-1">How to Accept Jobs?</p>
                  <p className="text-xs text-gray-600">You can accept jobs from your dashboard...</p>
                </div>
              </CardContent>
            </Card>

            {/* Platform Guidelines */}
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="text-base">Platform Guidelines</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Code of Conduct</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Service Policy</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Privacy & Data Security</span>
                </div>
              </CardContent>
            </Card>

            {/* My Open Cases */}
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="text-base">My Open Cases</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {openCases.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-600">No open support cases</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {openCases.map((t) => (
                      <div key={t.id} className="border-b pb-3 last:border-0">
                        <div className="flex items-start justify-between mb-1">
                          <p className="font-semibold text-gray-900 text-sm">#{t.id} {t.title}</p>
                          <Badge className="bg-yellow-100 text-yellow-800">{t.status}</Badge>
                        </div>
                        <p className="text-xs text-gray-500">{timeAgo(t.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Middle + Right Columns - Recently Resolved */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="text-base">Recently Resolved</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : resolvedCases.length === 0 ? (
                  <p className="text-sm text-gray-600 text-center py-4">No resolved tickets yet</p>
                ) : (
                  <div className="space-y-3">
                    {resolvedCases.map((t) => (
                      <div key={t.id} className="border-b pb-3 last:border-0">
                        <div className="flex items-start justify-between mb-1">
                          <p className="font-semibold text-gray-900 text-sm">#{t.id} {t.title}</p>
                          <Badge className="bg-green-100 text-green-800">Resolved</Badge>
                        </div>
                        <p className="text-xs text-gray-600">{t.category}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Opened: {timeAgo(t.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ProviderLayout>
  );
};

export default SupportCenterPage;
